import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Card,
  Divider,
  Typography,
  Input,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { MultiSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';

import messages from './messages';
import { FormGroup, HelpBlock } from 'react-bootstrap';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MITS_AMENITIES } from './constants';
import useIntl from '../../../hooks/useIntl';

type Props = {
  editMode: boolean,
  marketingInfo: Object,
  onChange: Function,
  disabled: boolean,
};

const MAX_LENGTH = 501;

const MarketingDetails = (props: Props) => {
  const intl = useIntl();
  const { syndicationFieldsContinental, propertyAmenities } = useFlags();
  const { editMode: editModeProp, marketingInfo, onChange, disabled } = props;

  const {
    pitch1,
    officeHoursDescription,
    propertyDescription,
    petPolicyDescription,
    parkingDetailDescription,
    amenities,
  } = marketingInfo;

  const editMode = editModeProp && !disabled;

  const validationState = React.useCallback(
    (value: string) => (editMode && !value.trim() ? 'error' : undefined),
    [editMode],
  );

  const requiredText = React.useCallback(
    (value: string) =>
      validationState(value) && (
        <HelpBlock>
          <FormattedMessage {...messages.required} />
        </HelpBlock>
      ),
    [validationState],
  );

  return (
    <Card sx={{ padding: 2, height: '100%' }}>
      <Typography variant={'titleLarge'}>
        <FormattedMessage {...messages.marketingDetails} />
      </Typography>
      <Divider sx={{ marginY: 2 }} />
      <Typography variant={'labelLarge'} display={'block'} mb={1}>
        <FormattedMessage {...messages.shortPropertyDescription} />*
      </Typography>
      <FormGroup validationState={validationState(pitch1)}>
        <textarea
          name="pitch1"
          className="form-control input-lg"
          value={pitch1}
          maxLength="255"
          disabled={!editMode}
          onChange={onChange}
        />
        {requiredText(pitch1)}
      </FormGroup>

      <Typography variant={'labelLarge'} display={'block'} mb={1}>
        <FormattedMessage {...messages.fullPropertyDescription} />
      </Typography>
      <FormGroup>
        <textarea
          name="propertyDescription"
          className="form-control input-lg"
          value={propertyDescription}
          maxLength="2500"
          disabled={!editMode}
          onChange={onChange}
        />
      </FormGroup>

      <Typography variant={'labelLarge'} display={'block'} mb={1}>
        <FormattedMessage {...messages.officeHours} />
      </Typography>
      <FormGroup>
        <input
          name="officeHoursDescription"
          className="form-control input-lg"
          value={officeHoursDescription}
          disabled={!editMode}
          onChange={onChange}
          maxLength="255"
        />
      </FormGroup>

      {syndicationFieldsContinental ? (
        <>
          <Typography variant={'labelLarge'} display={'block'} mb={1}>
            <FormattedMessage {...messages.petPolicyDescription} />
          </Typography>
          <FormGroup
            validationState={
              petPolicyDescription.length > MAX_LENGTH ? 'error' : undefined
            }
          >
            <Input
              multiline
              name="petPolicyDescription"
              value={petPolicyDescription}
              rows={3}
              maxLength={MAX_LENGTH}
              readOnly={!editMode}
              onChange={onChange}
              placeholder=""
              required={false}
              helperText={
                petPolicyDescription.length > MAX_LENGTH && (
                  <HelpBlock>
                    <FormattedMessage {...messages.maxLength} />
                  </HelpBlock>
                )
              }
            />
          </FormGroup>

          <Typography variant={'labelLarge'} display={'block'} mb={1}>
            <FormattedMessage {...messages.parkingDetailDescription} />
          </Typography>
          <FormGroup
            validationState={
              parkingDetailDescription.length > MAX_LENGTH ? 'error' : undefined
            }
          >
            <Input
              multiline
              name="parkingDetailDescription"
              value={parkingDetailDescription}
              rows={3}
              readOnly={!editMode}
              onChange={onChange}
              maxLength={MAX_LENGTH}
              placeholder=""
              required={false}
              helperText={
                parkingDetailDescription.length > MAX_LENGTH && (
                  <HelpBlock>
                    <FormattedMessage {...messages.maxLength} />
                  </HelpBlock>
                )
              }
            />
          </FormGroup>
        </>
      ) : null}

      {propertyAmenities ? (
        <>
          <Typography variant={'labelLarge'} display={'block'} mb={1}>
            <FormattedMessage {...messages.amenities} />
          </Typography>
          {editMode ? (
            <MultiSelect
              options={MITS_AMENITIES}
              value={amenities}
              onChange={(option) =>
                onChange({
                  target: { name: 'amenities', value: option },
                })
              }
              onClearSelected={() =>
                onChange({ target: { name: 'amenities', value: [] } })
              }
              placeholder={intl.formatMessage(messages.amenitiesPlaceholder)}
              fullWidth
            />
          ) : (
            <Typography
              variant={'bodyMedium'}
              display={'block'}
              mb={1}
              sx={
                amenities.length > 0
                  ? { color: 'text.primary' }
                  : { color: 'text.secondary' }
              }
            >
              {amenities.length > 0
                ? amenities
                    .map(
                      (amenity) =>
                        MITS_AMENITIES.find((a) => a.value === amenity)?.text ??
                        amenity,
                    )
                    .join(', ')
                : intl.formatMessage(messages.amenitiesPlaceholder)}
            </Typography>
          )}
        </>
      ) : null}
    </Card>
  );
};

export default MarketingDetails;
